/**
 * Test Connection runs of the connection dialog: the run token, the "Testing…" source, the watchdog, and the run
 * itself (moved out of `NewConnectionPopup`, C0; behaviour unchanged except that validation is the shared gate).
 *
 * The token moves on every new run, on `abort()` (Cancel test, dismissal, any C0 invalidation) and on unmount, so a run
 * that was superseded answers `discarded: true` and applies nothing — a late success can never save behind the user.
 * Round B R2: the answer is published under the snapshot taken when the run started; callers check
 * `matchesLive(snapshot)` before acting on it.
 */
import { useEffect, useRef, useState } from 'react';
import type { Connection } from '@/types/electron';
import type { TrackFunction } from '@/core/analytics/analyticsTypes';
import type { ConnectionFormValues } from '../../components/connectionFormSchema';
import type { ConnectionTestResult, ConnectionTestSnapshot, useConnectionTestOutcome } from '../../hooks/useConnectionTestOutcome';
import type { ConnectionForm } from '../state/connectionForm';
import { dialConnect, dialStepwise, dialStorage, type DialContext } from './connectionTestDial';
import { FUNNEL_VERSION } from './useConnectionDialogFunnel';

/**
 * How long a test may stay pending before the dialog offers an escape hatch (the win32 + named-instance + Windows-auth
 * chain can run minutes, which reads as a frozen dialog — SoporteIssues2.md Point 2).
 */
const TEST_WATCHDOG_MS = 25_000;

export type TestSource = 'test' | 'submit';

/** What a run was for, as `feature:connection_test_result.source` reports it (a fix chip's retest is `fix`). */
export type TestReportSource = TestSource | 'fix';

/** What a run answers; `snapshot` / `values` describe the tested form, including the run's own edits. */
export type ConnectionTestRun = {
  success: boolean;
  discarded: boolean;
  /** Validation stopped the run before any connect: callers measuring retry outcomes must not count it. */
  blocked?: boolean;
  snapshot?: ConnectionTestSnapshot;
  values?: ConnectionFormValues;
};

type Options = {
  form: ConnectionForm;
  tests: ReturnType<typeof useConnectionTestOutcome>;
  programmatic: (edit: () => void) => void;
  runGate: () => Promise<boolean>;
  probe: (values: ConnectionFormValues) => Promise<{ connection: Connection } | { error: string }>;
  track: TrackFunction;
};

export function useConnectionTestRun({ form, tests, programmatic, runGate, probe, track }: Options) {
  const [testRun, setTestRun] = useState<{ source: TestSource; token: number } | null>(null);
  const tokenRef = useRef(0);
  // The token of the run whose watchdog notice shows: a timer of a superseded run can never light it for the current one.
  const [watchdogToken, setWatchdogToken] = useState<number | null>(null);
  useEffect(() => () => { tokenRef.current += 1; }, []);
  useEffect(() => {
    if (!testRun) return;
    const { token } = testRun;
    const timer = window.setTimeout(() => setWatchdogToken(token), TEST_WATCHDOG_MS);
    return () => window.clearTimeout(timer);
  }, [testRun]);

  /** Cancel the in-flight run from any surface (dismissal, the watchdog's "Cancel test", a C0 invalidation). */
  const abort = () => {
    tokenRef.current += 1;
    setTestRun(null);
    setWatchdogToken(null);
  };

  /** Funnel v2 (C1): one event per run that answered for the form on screen — never a blocked or voided run. */
  const run = async (values: ConnectionFormValues, skipValidation = false, source: TestSource = 'test', reportAs: TestReportSource = source): Promise<ConnectionTestRun> => {
    const answer = await dial(values, skipValidation, source);
    if (!answer.blocked && !answer.discarded) {
      track('feature:connection_test_result', { engine: values.engine, outcome: answer.success ? 'success' : 'failure', source: reportAs, funnel_version: FUNNEL_VERSION });
    }
    return answer;
  };

  const dial = async (values: ConnectionFormValues, skipValidation: boolean, source: TestSource): Promise<ConnectionTestRun> => {
    if (!skipValidation && !(await runGate())) return { success: false, discarded: false, blocked: true };

    const token = ++tokenRef.current;
    const isCurrent = () => tokenRef.current === token;
    setTestRun({ source, token });
    let testedValues = form.state.values;
    let snapshot = tests.snapshotOf(testedValues);
    tests.clear(); // every earlier answer goes, whichever kind it was
    const show = (result: ConnectionTestResult) => {
      if (isCurrent()) tests.show(snapshot, { result });
    };
    const answer = (success: boolean): ConnectionTestRun => ({ success, discarded: !isCurrent(), snapshot, values: testedValues });

    try {
      const api = window.electronAPI;
      if (!api) throw new Error('Electron API is not available');
      const built = await probe(values);
      if ('error' in built) {
        show({ success: false, variant: 'warning', message: built.error });
        return answer(false);
      }
      const context: DialContext = { api, formValues: testedValues, connection: built.connection, isCurrent, tests, snapshot, track };
      if (values.engine === 'azurestorage') {
        const dialed = await dialStorage(context, form, programmatic);
        if (dialed.values && dialed.snapshot) {
          testedValues = dialed.values;
          snapshot = dialed.snapshot;
        }
        return answer(dialed.success);
      }
      if (values.sshTunnel?.enabled) return answer(await dialStepwise(context));
      return answer(await dialConnect(context));
    } catch (error) {
      show({ success: false, message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}` });
      return answer(false);
    } finally {
      if (isCurrent()) setTestRun(null);
    }
  };

  return {
    testRun,
    watchdogVisible: testRun !== null && watchdogToken === testRun.token,
    /** "Keep waiting": only the notice goes; the test keeps running. */
    dismissWatchdog: () => setWatchdogToken(null),
    abort,
    run,
  };
}
